"use client";

import { useRef, type ReactNode } from "react";
import { gsap, useGSAP } from "@/lib/gsap";

/**
 * Clip-path text reveal — text slides up from behind a mask.
 * Creates a premium "curtain rising" effect on section headings.
 *
 * Works by wrapping children in overflow:hidden + translating content up.
 * GSAP ScrollTrigger fires when element enters viewport.
 */
export default function TextReveal({
  children,
  className = "",
  delay = 0,
  tag: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  tag?: "div" | "h2" | "h3" | "p" | "span";
}) {
  const wrapRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!wrapRef.current) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const inner = wrapRef.current!.querySelector(".tr-inner");
        if (!inner) return;

        // Use gsap.from so the element stays visible if ScrollTrigger
        // never fires (e.g. element already in viewport on mount).
        gsap.from(inner, {
          yPercent: 110,
          duration: 0.8,
          delay,
          ease: "power3.out",
          scrollTrigger: {
            trigger: wrapRef.current,
            start: "top 90%",
            toggleActions: "play none none none",
            once: true,
          },
        });
      });
    },
    { scope: wrapRef },
  );

  return (
    <div ref={wrapRef} className={`overflow-hidden ${className}`}>
      <Tag className="tr-inner">
        {children}
      </Tag>
    </div>
  );
}
